import { setupUserAccess } from '@plugins/userAccess';
import {
  removeAvatar,
  updateNotificationSettings,
  getUser,
  updateUser,
  updateName,
  getUserNavData,
  confirmNewFeeStructure,
  fetchSiteWideSearchResults,
  updateUserContact,
  fetchMrktUpdates,
  updateProvider,
} from './provider-user.service';

export default {
  namespaced: true,

  state: {
    current: {},
    pages: [],
    luUser: false,
    siteWideSearchResults: [],
    mrktUpdates: {},
    navbarExpanded: localStorage.getItem('navbarExpanded') ? localStorage.getItem('navBarExpanded') === 'true' : true,
  },

  getters: {
    avatar: (state) => state.current.avatar || state.current.luAvatar,
    pages: (state) => state.pages,
    luUser: (state) => state.luUser,
    navbarExpanded: (state) => state.navbarExpanded,
    isNewFeesVersion(state) {
      return state.current.isNewFeesVersion === undefined
      || state.current.isNewFeesVersion === null ? true : state.current.isNewFeesVersion;
    },
    siteWideSearchResults: (state) => state.siteWideSearchResults,
    mrktUpdates: (state) => state.mrktUpdates,
    currentUser: (state) => state.current,
  },

  mutations: {
    current: (state, payload) => {
      state.current = {
        ...payload,
        luAvatar: state.current.luAvatar,
      };
    },

    clearAvatar(state) {
      state.current = { ...state.current, avatar: undefined, luAvatar: undefined };
    },

    navData: (state, payload) => {
      state.pages = payload.pages || [];
      state.current = { ...state.current, luAvatar: payload.avatar };
    },
    setMrktUpdates: (state, payload) => {
      state.mrktUpdates = payload;
    },
    setLuUser: (state, payload) => {
      state.luUser = payload;
    },
    setIsNewFeesVersion: (state, payload) => {
      state.current.isNewFeesVersion = payload;
    },
    setSiteWideSearchResults: (state, payload) => {
      state.siteWideSearchResults = payload;
    },
    toggleNavbar: (state) => {
      state.navbarExpanded = !state.navbarExpanded;
      localStorage.setItem('navbarExpanded', state.navbarExpanded);
    },
  },

  actions: {
    setCurrentUser({ commit }, user) {
      commit('current', user);
      // eslint-disable-next-line no-underscore-dangle
      setupUserAccess(user);
    },

    setNavData({ commit }, data) {
      commit('navData', data);
    },

    async updateName({ dispatch }, { firstName, lastName }) {
      try {
        await updateName({ firstName, lastName, userType: 'provider' });
        await dispatch('fetchCurrent');
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not update first and last name', error }, { root: true });
      }
    },

    async updateNotificationSettings(
      { dispatch },
      { phoneNumber, contactPreferenceSms, notifications },
    ) {
      try {
        await updateNotificationSettings({ phoneNumber, contactPreferenceSms, notifications });
        await dispatch('fetchCurrent');
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not update user settings', error }, { root: true });
      }
    },

    async fetchCurrent({ dispatch, state }) {
      try {
        const user = await getUser();
        dispatch('setCurrentUser', user);
        dispatch('track/identifyUser', {
          id: state.current.id,
          traits: state.current,
        }, { root: true });
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not load user', error }, { root: true });
      }
    },

    async fetchSiteWideSearchResults({ commit, dispatch }, { query }) {
      try {
        const results = await fetchSiteWideSearchResults({ query });
        commit('setSiteWideSearchResults', results);
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not fetch provider search results', error }, { root: true });
      }
    },

    async updateContact({ dispatch, state }, userObj) {
      try {
        const user = await updateUserContact(userObj);
        dispatch('setCurrentUser', user);
        dispatch('track/identifyUser', {
          id: state.current.id,
          traits: state.current,
        }, { root: true });
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not update user', error }, { root: true });
      }
    },

    async update({ dispatch, state }, userObj) {
      try {
        const user = await updateUser(userObj);
        dispatch('setCurrentUser', user);
        dispatch('track/identifyUser', {
          id: state.current.id,
          traits: state.current,
        }, { root: true });
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not update user', error }, { root: true });
      }
    },

    async removeAvatar({ commit, dispatch }) {
      try {
        await removeAvatar();
        commit('clearAvatar');
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not remove avatar', error }, { root: true });
        throw new Error();
      }
    },

    async fetchNavData({ dispatch }, email) {
      try {
        const data = await getUserNavData(email);
        dispatch('setNavData', data);
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not load navigation', error }, { root: true });
      }
    },
    async confirmNewFeeStructure({ commit, dispatch }, params) {
      try {
        const response = await confirmNewFeeStructure(params.id);
        commit('setIsNewFeesVersion', response.success);
        commit('providerFees/setFeesSettings', response.providerFees, { root: true });
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not acknowledge notification', error }, { root: true });
      }
    },
    async fetchMrktUpdates({ dispatch, commit, state }, { centerId }) {
      try {
        const response = await fetchMrktUpdates(state.current.providerId, centerId);
        commit('setMrktUpdates', response);
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not fetch dashboard updates', error }, { root: true });
      }
    },
    async updateProvider({ dispatch, state }, params) {
      try {
        await updateProvider(state.current.providerId, params);
      } catch (error) {
        dispatch('notifications/addToastError', { text: 'Could not update provider', error }, { root: true });
      }
    },
  },
};
